import {
  getGradingDisplayNames,
  type TeammateAccountGrading,
  type TranscriptSearchMatch,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Seconds } from "@repo/app-types/units/duration";
import { ShortAbsoluteTimestamp } from "@repo/ui/timestamps/shortAbsoluteTimestamp";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { ContactNameLines } from "./contactNameLines.js";
import { renderCallDuration } from "./durationFormatting.js";

//
// Props.
//

interface GradingsTableContactCellProps {
  grading: TeammateAccountGrading;
  /** Chronological transcript match to show when the row came from a search. */
  snippet?: TranscriptSearchMatch;
}

//
// Component.
//

export const GradingsTableContactCell: FC<GradingsTableContactCellProps> = (
  props,
) => {
  const { grading, snippet } = props;
  const {
    primaryDisplayName,
    secondaryDisplayName,
    isGeneratedFromTranscript,
  } = getGradingDisplayNames(grading);
  const callsInfo = renderCallsInfo(
    grading.recording_count,
    grading.recording_total_duration,
  );

  return (
    <>
      <ContactNameLines
        primaryDisplayName={primaryDisplayName}
        secondaryDisplayName={secondaryDisplayName}
        isGeneratedFromTranscript={isGeneratedFromTranscript}
      >
        <div className="flex items-center gap-1">
          <ShortAbsoluteTimestamp timestamp={grading.last_recorded_at} />
          {callsInfo && (
            <>
              <span className="text-xs text-gray-400">•</span>
              {callsInfo}
            </>
          )}
        </div>
      </ContactNameLines>

      {snippet ? (
        <p
          className={twMerge(
            "mt-0.5 line-clamp-2 text-xs text-gray-500",
            PH_MASK_CLASS,
          )}
        >
          {renderHighlightedSnippet(
            snippet.snippet.text,
            snippet.snippet.highlights,
          )}
        </p>
      ) : null}
    </>
  );
};

//
// Helpers.
//

/**
 * Renders `snippet`, wrapping the server-computed `highlights` offset ranges in
 * `<mark>`. Ranges are non-overlapping and in order; anything between them (and
 * the tail) renders as plain text.
 */
function renderHighlightedSnippet(
  snippet: string,
  highlights: readonly { start: number; end: number }[],
): ReactNode {
  if (highlights.length === 0) {
    return snippet;
  }

  const parts: ReactNode[] = [];
  let cursor = 0;
  highlights.forEach(({ start, end }, index) => {
    if (start > cursor) {
      parts.push(snippet.slice(cursor, start));
    }
    parts.push(
      <mark
        key={index}
        className="rounded bg-amber-200/70 px-0.5 text-amber-900"
      >
        {snippet.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < snippet.length) {
    parts.push(snippet.slice(cursor));
  }
  return parts;
}

function renderCallsInfo(
  totalCalls: number,
  totalDurationSeconds: Seconds,
): ReactNode {
  if (totalCalls === 0) {
    return null;
  }

  const totalDurationFormatted = renderCallDuration(totalDurationSeconds);

  return (
    <div className="flex items-center gap-1 text-xs whitespace-nowrap text-gray-600">
      <span>
        {totalCalls} call{totalCalls !== 1 ? "s" : ""} ({totalDurationFormatted}
        )
      </span>
    </div>
  );
}
